import React from 'react';
import {
  FaFire,
  FaRocket,
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const SlideTwo = () => {
  const { t } = useTranslation();

  const benefits = [
    {
      emoji: '🧠',
      title: t('landing.slideTwo.benefits.aiLearning.title', 'Outcome-Based Curriculum'),
      description: t('landing.slideTwo.benefits.aiLearning.description', 'Define your goal and get a step-by-step curriculum with milestones'),
      color: 'from-blue-400 to-blue-500',
    },
    {
      emoji: '🎓',
      title: t('landing.slideTwo.benefits.expertKnowledge.title', 'Clear Explanations'),
      description: t('landing.slideTwo.benefits.expertKnowledge.description', 'Concepts explained with examples, summaries, and practical context'),
      color: 'from-purple-400 to-purple-500',
    },
    {
      emoji: '📁',
      title: t('landing.slideTwo.benefits.communityLearning.title', 'Grounded teaching'),
      description: t('landing.slideTwo.benefits.communityLearning.description', 'Courses built from your uploaded sources — not generic AI fluff'),
      color: 'from-indigo-400 to-indigo-500',
    },
    {
      emoji: '🔒',
      title: t('landing.slideTwo.benefits.securePlatform.title', 'Progress Tracking'),
      description: t('landing.slideTwo.benefits.securePlatform.description', 'Stay consistent with progress checkpoints and structured modules'),
      color: 'from-pink-400 to-pink-500',
    },
  ];

  const useCases = [
    {
      emoji: '👨‍🎓',
      title: t('landing.slideTwo.useCases.students.title', 'For Students'),
      description: t('landing.slideTwo.useCases.students.description', 'Master any subject at your own pace. From exam prep to exploring new topics, make learning effective and enjoyable.'),
      color: 'from-blue-400 to-blue-500',
    },
    {
      emoji: '👩‍🏫',
      title: t('landing.slideTwo.useCases.teachers.title', 'For Teachers'),
      description: t('landing.slideTwo.useCases.teachers.description', 'Create comprehensive courses in minutes. Save time, boost engagement, and deliver exceptional learning experiences with AI-powered content.'),
      color: 'from-purple-400 to-purple-500',
    },
    {
      emoji: '🚀',
      title: t('landing.slideTwo.useCases.careerGrowth.title', 'Career & Upskilling'),
      description: t('landing.slideTwo.useCases.careerGrowth.description', 'Build job-ready skills with a clear learning plan and practice'),
      color: 'from-indigo-400 to-indigo-500',
    },
    {
      emoji: '📈',
      title: t('landing.slideTwo.useCases.skillEnhancement.title', 'Exam & Certification Prep'),
      description: t('landing.slideTwo.useCases.skillEnhancement.description', 'Turn a syllabus into modules, quizzes, and revision checkpoints'),
      color: 'from-pink-400 to-pink-500',
    },
  ];

  const learningSteps = [
    { emoji: '💡', label: t('landing.slideTwo.learningSteps.remember', 'Remember'), color: 'from-yellow-400 to-yellow-500' },
    { emoji: '✏️', label: t('landing.slideTwo.learningSteps.apply', 'Apply'), color: 'from-blue-400 to-blue-500' },
    { emoji: '🏆', label: t('landing.slideTwo.learningSteps.master', 'Master'), color: 'from-purple-400 to-purple-500' },
  ];

  return (
    <main className="w-full bg-white dark:bg-slate-950">
      {/* Section 1: Why it works - Duolingo style */}
      <section className="relative py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/20 to-white dark:from-gray-950 dark:via-blue-950/5 dark:to-gray-950" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-yellow-100 dark:from-blue-900/30 dark:to-yellow-900/20 rounded-full border-2 border-blue-300 dark:border-blue-700 mb-4 shadow-sm"
            >
              <FaFire className="text-blue-600 dark:text-blue-400 text-sm" />
              <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                {t('landing.slideTwo.learningScience.badge', 'Backed by learning science')}
              </span>
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 leading-tight tracking-tight">
              {t('landing.slideTwo.benefits.title', 'Learning that actually sticks')}
            </h2>
            <p className="text-lg md:text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('landing.slideTwo.benefits.subtitle', 'A learning system—not just more content')}
            </p>
          </div>

          {/* Learning Steps - Duolingo colorful */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-12"
          >
            {learningSteps.map((step, i) => (
              <React.Fragment key={i}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, rotate: -10 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ delay: 0.3 + i * 0.1, type: 'spring' }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className={`flex flex-col items-center gap-2 px-6 py-4 bg-gradient-to-br ${step.color} rounded-2xl shadow-lg border-2 border-white/50 dark:border-gray-800/50 hover:shadow-xl transition-all`}
                >
                  <span className="text-4xl">{step.emoji}</span>
                  <span className="text-xs font-bold text-white uppercase tracking-wide drop-shadow-sm">{step.label}</span>
                </motion.div>
                {i < learningSteps.length - 1 && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                    className="text-2xl text-blue-400 font-bold hidden sm:block"
                  >
                    →
                  </motion.span>
                )}
              </React.Fragment>
            ))}
          </motion.div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.1, duration: 0.6 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group p-5 bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${b.color} flex items-center justify-center text-3xl mb-3 shadow-md`}>
                  {b.emoji}
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">{b.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{b.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: Perfect for - Use Cases */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-indigo-50/20 to-white dark:from-gray-950 dark:via-indigo-950/5 dark:to-gray-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/20 rounded-full border-2 border-indigo-300 dark:border-indigo-700 mb-4 shadow-sm"
            >
              <FaRocket className="text-indigo-600 dark:text-indigo-400 text-sm" />
              <span className="text-xs font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">
                {t('landing.slideTwo.useCases.badge', 'Perfect for')}
              </span>
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 leading-tight tracking-tight">
              {t('landing.slideTwo.useCases.title', 'Built for everyone')}
            </h2>
            <p className="text-lg md:text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('landing.slideTwo.useCases.subtitle', 'Whether you\'re a student, teacher, or lifelong learner—we\'ve got you covered.')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {useCases.map((uc, i) => (
              <motion.div
                key={uc.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.1, duration: 0.6 }}
                whileHover={{ y: -5, scale: 1.01 }}
                className="group relative flex gap-5 p-6 bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex-shrink-0">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${uc.color} flex items-center justify-center text-4xl shadow-lg`}>
                    {uc.emoji}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{uc.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{uc.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default SlideTwo;
